import React, { useCallback, useEffect, useState } from "react";
import { Siren, Clock, User, AlertTriangle, Phone, Paperclip } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
function elapsed(iso) {
  if (!iso) return "—";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function DocsCell({ cbId }) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/callbacks/${cbId}/attachments`)
      .then((r) => { if (alive) setCount((r.data.items || []).length); })
      .catch(() => { if (alive) setCount(0); });
    return () => { alive = false; };
  }, [cbId]);
  const isEmpty = count === 0;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[12px] ${isEmpty ? "text-[#9CA3AF]" : "text-[#0B7E8C] font-semibold"}`}
      data-testid={`escalated-docs-${cbId}`}
    >
      <Paperclip className="w-3.5 h-3.5" />
      {count === null ? "…" : count}
    </span>
  );
}

export default function EscalatedCallbacks() {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get("/callbacks/escalated")
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="escalated-callbacks-page">
      <PageHeader
        title="Escalated Callbacks"
        subtitle="Callback requests that exceeded their SLA and now need coordinator or facility-manager intervention."
        icon={Siren}
      />
      {items === null ? (
        <div className="text-sm text-[#6B7280]" data-testid="escalated-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hah-card p-6 text-sm text-[#6B7280]" data-testid="escalated-empty">
          No escalated callbacks. When a callback isn't acknowledged within its SLA window, it will appear here.
        </div>
      ) : (
        <div className="hah-card overflow-hidden">
          <table className="w-full text-sm" data-testid="escalated-table">
            <thead className="bg-[#F9FAFB] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-2.5">Patient</th>
                <th className="px-4 py-2.5">Urgency</th>
                <th className="px-4 py-2.5">Requested for</th>
                <th className="px-4 py-2.5">Assigned to</th>
                <th className="px-4 py-2.5">Escalated</th>
                <th className="px-4 py-2.5">Docs</th>
                <th className="px-4 py-2.5">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {items.map((cb) => (
                <tr key={cb.id} data-testid={`escalated-row-${cb.id}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-medium text-[#1F2937]">
                      <User className="w-4 h-4 text-[#6B7280]" />
                      {cb.patient_name || "Patient"}
                    </div>
                    {cb.patient_phone && (
                      <a href={`tel:${cb.patient_phone}`}
                         className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-[#0B7E8C] hover:text-[#128C97]"
                         data-testid={`escalated-patient-phone-${cb.id}`}>
                        <Phone className="w-3 h-3" />{cb.patient_phone}
                      </a>
                    )}
                    <div className="text-[11px] text-[#9CA3AF]">Facility {cb.facility_id?.slice(0, 8) || "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${
                      cb.urgency === "urgent" ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 text-slate-700 border border-slate-200"
                    }`}>
                      {cb.urgency === "urgent" && <AlertTriangle className="w-3 h-3" />}{cb.urgency}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#374151]">
                    <span className="text-[12px] font-semibold">
                      { { stat: "STAT", morning: "Morning", afternoon: "Afternoon", evening: "Evening", second_day: "Second Day" }[cb.preferred_time] || (cb.preferred_time || "").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#374151]">{cb.assigned_to_name}</div>
                    <div className="text-[11px] text-[#9CA3AF] capitalize">{(cb.assigned_to_role || "").replace(/_/g, " ")}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-[#374151] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {elapsed(cb.escalated_at)}</div>
                    <div className="text-[11px] text-[#9CA3AF]">SLA {cb.sla_minutes}m · Missed {fmt(cb.sla_deadline_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <DocsCell cbId={cb.id} />
                  </td>
                  <td className="px-4 py-3 max-w-[320px]">
                    <div className="text-[#374151] whitespace-pre-wrap line-clamp-3">{cb.reason}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
